import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import left from "../../../assets/icons/Arrow/left.svg";
import help from "../../../assets/icons/System/help.svg";
import { FillButton, GrayButton, TwinColumn } from "../../../assets/components/ButtonGroup";
import Popup from "../../../assets/components/PopupGroup";
import { useToast } from "../../../hooks/useToast";
import {
  getEffectiveRepresentativeMenus,
  setEditedRepresentativeMenus,
} from "../../../utils/representativeMenus";
import { fetchSiksinMenusByBusinessNumber } from "../../../utils/siksinMenus";
import {
  fetchConciergeImagesByBusinessNumber,
  updateConciergeImagesByBusinessNumber,
} from "../../../utils/conciergeImages";

const MAX_IMAGE_COUNT = 6;

const MyInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { headerRef, footerRef, headerSpace, bottomSpace } = useFixedLayout();
  const fileInputRef = useRef(null);

  const state = location.state;
  const info = state?.info || {};
  const roadName = state?.roadName || "";
  const data = state?.data || {};
  const registerTag = useMemo(() => {
    return (
      info?.register_tag ||
      data?.register_tag ||
      localStorage.getItem("registerTag") ||
      ""
    );
  }, [data?.register_tag, info?.register_tag]);

  const storeBusinessNumber = useMemo(() => {
    return data?.store_business_number || localStorage.getItem("store_business_number") || "";
  }, [data?.store_business_number]);

  const [menus, setMenus] = useState([]);
  const [initialMenus, setInitialMenus] = useState([]);
  const [conciergeUserId, setConciergeUserId] = useState(0);
  const [serverImages, setServerImages] = useState([]);
  const [removedServerIds, setRemovedServerIds] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newMenu, setNewMenu] = useState("");

  useEffect(() => {
    return () => {
      newImages.forEach((item) => {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {
          // ignore
        }
      });
    };
  }, [newImages]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const [fetchedMenus, conciergeData] = await Promise.all([
          fetchSiksinMenusByBusinessNumber(storeBusinessNumber),
          fetchConciergeImagesByBusinessNumber(storeBusinessNumber),
        ]);
        if (!alive) return;

        const normalizedMenus = Array.isArray(fetchedMenus) ? fetchedMenus : [];
        const conciergeMenus = [
          conciergeData?.menu1,
          conciergeData?.menu2,
          conciergeData?.menu3,
        ]
          .map((v) => String(v ?? "").trim())
          .filter(Boolean);
        const effectiveMenus = getEffectiveRepresentativeMenus({
          storeBusinessNumber,
          dbMenus: normalizedMenus,
          conciergeMenus,
          registerTag,
        });
        setMenus(effectiveMenus);
        setInitialMenus(effectiveMenus);
        setConciergeUserId(Number(conciergeData.userId || 0));
        setServerImages(Array.isArray(conciergeData.images) ? conciergeData.images : []);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [registerTag, storeBusinessNumber]);

  const visibleServerImages = useMemo(() => {
    return serverImages.filter((img) => !removedServerIds.includes(img.file_id));
  }, [serverImages, removedServerIds]);

  const remainingSlots = Math.max(0, MAX_IMAGE_COUNT - visibleServerImages.length - newImages.length);
  const menuChanged = JSON.stringify(menus) !== JSON.stringify(initialMenus);
  const imageChanged = removedServerIds.length > 0 || newImages.length > 0;
  const canSave = imageChanged || (menus.length > 0 && menuChanged);

  const infoRows = [
    { label: "매장명", value: data.store_name || "" },
    { label: "매장주소", value: roadName || "" },
    { label: "대표자 이름", value: data.business_name || "" },
  ];

  const goMypage = () => {
    navigate("/ads/main", { state: { tab: "mypage" } });
  };

  const goInquiry = () => {
    navigate("/ads/inquiry", {
      state: {
        name: data.business_name || "",
        phone: info.phone || "",
        from: { pathname: location.pathname, state: location.state ?? null },
      },
    });
  };

  const updateMenuAt = (index, nextValue) => {
    setMenus((prev) => {
      const next = [...prev];
      next[index] = String(nextValue ?? "");
      return next;
    });
  };

  const removeMenuAt = (index) => {
    setMenus((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmAddMenu = () => {
    const trimmed = String(newMenu ?? "").trim();
    if (!trimmed) return;
    setMenus((prev) => {
      if (prev.includes(trimmed)) return prev;
      return [...prev, trimmed];
    });
    setNewMenu("");
    setAddOpen(false);
  };

  const openFilePicker = () => {
    if (remainingSlots <= 0) {
      window.alert(`이미지는 최대 ${MAX_IMAGE_COUNT}장까지 등록할 수 있습니다.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleSelectFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const available = MAX_IMAGE_COUNT - visibleServerImages.length - newImages.length;
    const picked = files.slice(0, Math.max(0, available));
    if (picked.length < files.length) {
      window.alert(`이미지는 최대 ${MAX_IMAGE_COUNT}장까지 등록할 수 있습니다.`);
    }

    const nextItems = picked.map((file) => ({
      tempId: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setNewImages((prev) => [...prev, ...nextItems]);
  };

  const handleRemoveServerImage = (fileId) => {
    setRemovedServerIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  };

  const handleRestoreServerImage = (fileId) => {
    setRemovedServerIds((prev) => prev.filter((id) => id !== fileId));
  };

  const handleRemoveNewImage = (tempId) => {
    setNewImages((prev) => {
      const target = prev.find((item) => item.tempId === tempId);
      if (target) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {
          // ignore
        }
      }
      return prev.filter((item) => item.tempId !== tempId);
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (imageChanged) {
        if (!conciergeUserId && serverImages.length === 0) {
          throw new Error("컨시어지 매장 연결 정보가 없어 이미지를 저장할 수 없습니다.");
        }

        const imageResult = await updateConciergeImagesByBusinessNumber({
          storeBusinessNumber,
          removedFileIds: removedServerIds,
          newFiles: newImages.map((item) => item.file),
        });

        setConciergeUserId(Number(imageResult.userId || 0));
        setServerImages(Array.isArray(imageResult.images) ? imageResult.images : []);
        setRemovedServerIds([]);
        newImages.forEach((item) => {
          try {
            URL.revokeObjectURL(item.previewUrl);
          } catch {
            // ignore
          }
        });
        setNewImages([]);
      }

      if (menuChanged) {
        setEditedRepresentativeMenus(storeBusinessNumber, menus);
        setInitialMenus(menus);
      }

      showToast("내정보를 저장했어요.", {
        placement: "bottom",
        bottomOffset: "88px",
      });
    } catch (error) {
      window.alert(error?.response?.data?.detail || error.message || "저장에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-dvh bg-surface-light-white">
      <div className="fixed top-0 left-0 right-0 z-20" ref={headerRef}>
        <HeaderIconL className="headerRef" title="내정보 수정" icon={left} onIconClick={goMypage} />
      </div>

      <div className="p-[16px]" style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}>
        <div className="flex justify-between py-[16px]">
          <h2 className="text-t2 text-text-subtle">기본 정보</h2>
          <button onClick={() => setOpen(true)} className="underline text-b2 text-text-subtler">
            수정 요청
          </button>
        </div>

        <Popup
          isOpen={open}
          onClose={() => setOpen(false)}
          title="기본 정보 수정 요청"
          subtitle={"변경사항은 문의로 전달해 주세요.\n\n문의 : 02-2655-9600"}
          renderButtons={
            <TwinColumn
              topType="default"
              topText="1:1 문의 남기기"
              onTop={goInquiry}
              bottomType="empty"
              bottomText="닫기"
              onBottom={() => setOpen(false)}
            />
          }
        />

        <div className="px-[12px] gap-[12px] w-full rounded-[16px] border border-border-subtle bg-surface-light-white-layered">
          {infoRows.map((item, idx) => (
            <div
              key={item.label}
              className={`flex py-[12px] text-c2 ${idx !== infoRows.length - 1 ? "border-b border-border-subtle" : ""}`}
            >
              <div className="w-[80px] shrink-0 text-text-subtler">{item.label}</div>
              <div className="pl-[24px] flex-1 text-text-subtle leading-relaxed">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="pt-[32px]">
          <div className="flex items-center gap-2">
            <div className="text-b2 text-text-subtle">
              대표 메뉴
              <span className="text-text-primary">*</span>
            </div>
            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center justify-center w-[20px] h-[20px]"
                onClick={() => setTooltipOpen((v) => !v)}
              >
                <img src={help} alt="help" className="w-[16px] h-[16px]" />
              </button>
              {tooltipOpen && (
                <div className="absolute left-full ml-2 bottom-0 z-20 w-[220px] rounded-[8px] bg-black text-white text-[12px] px-3 py-2">
                  이미지 생성과 문구 추천에 사용할 대표 메뉴를 관리해 주세요.
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {menus.map((menu, idx) => (
              <div
                key={`menu-${idx}`}
                className="inline-flex items-center gap-1 h-8 px-[12px] rounded-[12px] border bg-surface-light-white border-border-default"
              >
                <input
                  value={menu}
                  onChange={(e) => updateMenuAt(idx, e.target.value)}
                  className="bg-transparent outline-none text-c2 text-text-subtle w-[90px]"
                />
                <button
                  type="button"
                  className="text-text-subtler text-c2"
                  onClick={() => removeMenuAt(idx)}
                >
                  X
                </button>
              </div>
            ))}

            <button
              type="button"
              className="inline-flex items-center justify-center h-8 px-[12px] rounded-[12px] border border-border-default bg-surface-light-white text-c2 text-text-subtle"
              onClick={() => setAddOpen(true)}
            >
              +
            </button>
          </div>
        </div>

        <div className="pt-[32px]">
          <div>
            <div className="text-b2 text-text-subtle">매장 사진</div>
            <p className="mt-1 text-c2 text-text-subtler">
              컨시어지에 등록한 매장 사진을 앱과 웹에서 함께 사용합니다.
            </p>
          </div>

          <div className="mt-4 rounded-[20px] border border-border-subtle bg-surface-light-white-layered p-[14px]">
            <div className="mb-3 flex items-center justify-between text-c2 text-text-subtler">
              <span>최대 {MAX_IMAGE_COUNT}장</span>
              <span>{visibleServerImages.length + newImages.length}/{MAX_IMAGE_COUNT}</span>
            </div>

            {visibleServerImages.length === 0 && newImages.length === 0 ? (
              <button
                type="button"
                onClick={openFilePicker}
                className="flex w-full min-h-[176px] flex-col items-center justify-center rounded-[18px] border border-dashed border-border-default bg-white text-text-subtler"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F0FF] text-[26px] text-[#7864F9]">
                  +
                </div>
                <div className="text-b2 text-text-subtle">첫 매장 사진을 등록해 보세요</div>
                <div className="mt-1 text-c2 text-text-subtler">메인 배너와 컨시어지 관리 화면에 함께 반영됩니다.</div>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {serverImages.map((image) => {
                  const removed = removedServerIds.includes(image.file_id);
                  return (
                    <div
                      key={`server-${image.file_id}`}
                      className={`relative overflow-hidden rounded-[18px] bg-[#F5F6FA] ${removed ? "opacity-50" : ""}`}
                    >
                      <img
                        src={image.public_url || image.storage_path}
                        alt={image.original_name || "매장 사진"}
                        className="h-[150px] w-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 py-3">
                        <div className="truncate pr-2 text-[12px] text-white">
                          {image.original_name || "매장 사진"}
                        </div>
                        {removed ? (
                          <button
                            type="button"
                            onClick={() => handleRestoreServerImage(image.file_id)}
                            className="rounded-full bg-white/90 px-3 py-1 text-[12px] font-semibold text-[#3B3F53]"
                          >
                            복원
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveServerImage(image.file_id)}
                            className="rounded-full bg-black/60 px-3 py-1 text-[12px] font-semibold text-white"
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {newImages.map((image) => (
                  <div key={image.tempId} className="relative overflow-hidden rounded-[18px] bg-[#F5F6FA]">
                    <img src={image.previewUrl} alt={image.file.name} className="h-[150px] w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 py-3">
                      <div className="truncate pr-2 text-[12px] text-white">새 사진</div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(image.tempId)}
                        className="rounded-full bg-black/60 px-3 py-1 text-[12px] font-semibold text-white"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}

                {Array.from({ length: remainingSlots }).map((_, idx) => (
                  <button
                    key={`empty-${idx}`}
                    type="button"
                    onClick={openFilePicker}
                    className="flex h-[150px] flex-col items-center justify-center rounded-[18px] border border-dashed border-border-default bg-white text-text-subtler"
                  >
                    <div className="mb-2 text-[28px] text-[#7864F9]">+</div>
                    <div className="text-c1 text-text-subtle">사진 추가</div>
                  </button>
                ))}
              </div>
            )}

            {!conciergeUserId && serverImages.length === 0 ? (
              <div className="mt-3 rounded-[14px] bg-[#FFF7ED] px-4 py-3 text-[12px] leading-5 text-[#9A5B13]">
                컨시어지 매장 연결이 아직 없으면 저장 시 사진 반영이 제한될 수 있습니다.
              </div>
            ) : null}
          </div>
        </div>

        <Popup
          isOpen={addOpen}
          onClose={() => {
            setAddOpen(false);
            setNewMenu("");
          }}
          title="대표 메뉴 추가"
          subtitle="추가할 메뉴명을 입력해 주세요."
          renderButtons={
            <TwinColumn
              topType="fill"
              topText="추가"
              onTop={confirmAddMenu}
              bottomType="empty"
              bottomText="닫기"
              onBottom={() => {
                setAddOpen(false);
                setNewMenu("");
              }}
            />
          }
        >
          <div className="w-full">
            <input
              value={newMenu}
              onChange={(e) => setNewMenu(e.target.value)}
              className="w-full rounded-md border border-border-subtle px-3 py-2 outline-none"
              placeholder="예: 아메리카노"
            />
          </div>
        </Popup>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleSelectFiles}
        />

        <div ref={footerRef} className="fixed bottom-0 left-0 right-0 z-10">
          {canSave ? <FillButton onClick={handleSave}>저장</FillButton> : <GrayButton disabled>저장</GrayButton>}
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/10 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 rounded bg-white px-4 py-2 shadow">
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" d="M4 12a8 8 0 018-8v4" fill="currentColor" />
            </svg>
            <span className="text-sm text-gray-700">불러오는 중...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyInfo;
